import { jsx, jsxs } from 'react/jsx-runtime';
import { createContext, forwardRef, useRef, useContext, useLayoutEffect, useCallback, useState, useMemo, useEffect, useId } from 'react';
import { flushSync } from 'react-dom';
import { FloatingPortal, FloatingOverlay, FloatingFocusManager, useListItem, useFloating, offset, flip, shift, size, inner, autoUpdate, useInteractions, useClick, useDismiss, useInnerOffset, useListNavigation, useTypeahead, FloatingList } from '@floating-ui/react';
import clsx from 'clsx';
import { EventShield } from '../event-shield/event-shield.js';
import { useProvidedId, useIdProvider } from '../hooks/id-provider.js';
import { useDebounce } from '../hooks/use-debounce.js';
import { useInputChange } from '../hooks/use-input-change.js';
import { useInteractionHandler, joinKeys } from '../hooks/use-interaction-handler.js';
import { useMergeRefs } from '../hooks/use-merge-refs.js';
import { useValidatedContext } from '../hooks/use-validated-context.js';
import { getScrollablePopupScrollPosition, SCROLLABLE_POPUP_SCROLL_MARGIN } from '../scrollable-popup/scrollable-popup.utils.js';
import { makeHandleCtrlNav } from '../utils/ctrl-nav.js';
import { PREVENT_WHEEL_CAPTURE, PREVENT_KEY_CAPTURE, PREVENT_EVENT_CAPTURE, WHEEL_EVENT_PASSTHROUGH, accept } from '../utils/event.js';
import { isNonNullishPrimitive } from '../utils/object.js';
import { observeResize } from '../utils/observe-resize.js';
import { PopoverOutletContext } from '../utils/popover-outlet.js';
import { disablePageScroll } from '../utils/scroll.js';
import { resolveStyleValue } from '../utils/style-value.js';
import { getItemLabel, normalizeTypeaheadText } from '../utils/typeahead.js';
import { getPlacementFromPosition, createPositionMiddleware } from './select-primitive.positioning.js';
import { scrollArrow, container, overlay } from './select-primitive.css.js';

/** Context used to provide select information to other primitives. */
const SelectPrimitiveContext = createContext(null);
/** @see https://fpl.figma.design/primitives/single-select/ */
function useSelect() {
    const { itemList, selectedIndex, isOpen, activeIndex, triggerLabel } = useSelectContext('useSelect');
    return {
        itemList,
        selectedItem: selectedIndex !== null ? itemList[selectedIndex] : null,
        isOpen,
        highlightedItem: activeIndex !== null ? itemList[activeIndex] : null,
        triggerLabel,
    };
}
/**
 * Arrow visibility is CSS-driven off `data-fpl-scroll-y` on the listbox (set by `Container` from
 * the shared `computeScrollPosition` util), mirroring menu-v2. This component only owns the
 * hover-scroll behavior.
 *
 * @see https://fpl.figma.design/primitives/single-select/
 */
const ScrollArrow = forwardRef(({ direction, children, className, ...props }, ref) => {
    const frameRef = useRef(-1);
    const { scrollRef, handleArrowScroll } = useSelectContext('ScrollArrow');
    const handlePointerEnter = () => {
        let prevNow = Date.now();
        function frame() {
            if (scrollRef.current) {
                const currentNow = Date.now();
                const msElapsed = currentNow - prevNow;
                prevNow = currentNow;
                const pixelsToScroll = msElapsed / 2;
                const remainingPixels = direction === 'up'
                    ? scrollRef.current.scrollTop
                    : scrollRef.current.scrollHeight -
                        scrollRef.current.clientHeight -
                        scrollRef.current.scrollTop;
                // Hiding the arrow causes pointerleave and stops this loop. Finish scrolling to the
                // edge in the same frame that enters the arrow's visibility margin.
                const scrollAmount = remainingPixels - pixelsToScroll <= SCROLLABLE_POPUP_SCROLL_MARGIN
                    ? remainingPixels
                    : pixelsToScroll;
                handleArrowScroll(direction === 'up' ? scrollAmount : -scrollAmount);
                if (scrollAmount < remainingPixels) {
                    frameRef.current = requestAnimationFrame(frame);
                }
            }
        }
        cancelAnimationFrame(frameRef.current);
        frameRef.current = requestAnimationFrame(frame);
    };
    const handlePointerLeave = () => {
        cancelAnimationFrame(frameRef.current);
    };
    return (jsx("div", { "data-fpl-component": "primitive", className: clsx(scrollArrow, className), "data-fpl-select-direction": direction, ref: ref, "aria-hidden": true, onPointerEnter: handlePointerEnter, onPointerLeave: handlePointerLeave, ...props, children: children }));
});
ScrollArrow.displayName = 'SelectPrimitive.ScrollArrow';
/** @see https://fpl.figma.design/primitives/single-select/ */
const Container = forwardRef(({ children, className, styles: s, DEPRECATED_optionsOnFocusOnly, ...props }, refIn) => {
    const wrapperStyle = resolveStyleValue(s?.wrapper);
    const { setContainerReference, getContainerProps, floatingContext, floatingStyles, scrollRef, fallback, setScrollTop, isOpen, isTriggerFocused, innerOffset, scrollTop, } = useSelectContext('Container');
    const ref = useMergeRefs(refIn, scrollRef, initializeScrollPosition);
    const rootRef = useContext(PopoverOutletContext);
    useLayoutEffect(() => {
        if (!isOpen)
            return;
        // Note: we only want to run this when we render the FloatingOverlay component
        return disablePageScroll();
    }, [isOpen]);
    // Vertical scroll position drives scroll-arrow visibility via CSS (`data-fpl-scroll-y`). This
    // value is derived entirely from the listbox's DOM measurements and has no React consumers, so
    // write it directly to the element instead of causing a React render for every change.
    const updateScrollPosition = useCallback(() => {
        setScrollPositionAttribute(scrollRef.current);
    }, [scrollRef]);
    const { isPositioned } = floatingContext;
    // Both the open sequence and resize-driven layout update settle the listbox's scroll over several
    // frames: the `inner` middleware plus `useListNavigation`'s scroll-selected-into-view on open,
    // and the middleware growing/clamping the list height on resize (default inner-offset mode). An
    // intermediate frame can momentarily report a mid-list `scrollTop` (→ 'middle') even though the
    // list comes to rest at an edge, so a single immediate measurement can latch onto that transient
    // and leave a stale arrow (classically a down chevron that lingers at the true bottom). Sample
    // across frames until the computed position holds steady, then commit it once. A ref-held raf
    // lets a later trigger restart an in-flight settle instead of racing it, so resize events funnel
    // through the same sampling rather than stomping state with a transient value.
    const settleRafRef = useRef(-1);
    const settleScrollPosition = useCallback(() => {
        cancelAnimationFrame(settleRafRef.current);
        let previous = null;
        let stableFrames = 0;
        let frames = 0;
        const settle = () => {
            const next = getScrollablePopupScrollPosition(scrollRef.current);
            stableFrames = next === previous ? stableFrames + 1 : 0;
            previous = next;
            frames += 1;
            if (stableFrames >= 2 || frames >= 30) {
                setScrollPositionAttribute(scrollRef.current, next);
                return;
            }
            settleRafRef.current = requestAnimationFrame(settle);
        };
        settleRafRef.current = requestAnimationFrame(settle);
    }, [scrollRef]);
    useLayoutEffect(() => {
        if (!isOpen || !isPositioned)
            return;
        settleScrollPosition();
        return () => cancelAnimationFrame(settleRafRef.current);
    }, [isOpen, isPositioned, innerOffset, scrollTop, settleScrollPosition]);
    // In default (inner-offset) mode the middleware grows/clamps the list's height without always
    // firing a scroll event, so watch for size changes too. Resize can span several frames, so route
    // it through the settle sampling rather than committing each transient size immediately.
    useLayoutEffect(() => {
        if (!isOpen) {
            // The listbox stays mounted while closed, so reset its attribute to the neutral default
            // before the next open measures its new position.
            setScrollPositionAttribute(scrollRef.current, 'none');
            return;
        }
        const disconnect = observeResize(scrollRef, () => settleScrollPosition());
        return () => {
            disconnect();
            cancelAnimationFrame(settleRafRef.current);
        };
    }, [isOpen, scrollRef, settleScrollPosition]);
    const listBox = (jsx(EventShield, { display: "contents", eventListeners: ['onClick', 'onPointerDown', 'onMouseDown'], ...PREVENT_WHEEL_CAPTURE, children: jsx("div", { "data-fpl-component": "primitive", ref: setContainerReference, className: wrapperStyle.className, style: { ...wrapperStyle.style, ...floatingStyles }, children: jsx("ul", { className: clsx(container, className), ref: ref, "data-fpl-fallback": fallback, role: "listbox", ...getContainerProps({
                    onScroll({ currentTarget }) {
                        flushSync(() => setScrollTop(currentTarget.scrollTop));
                        updateScrollPosition();
                    },
                }), ...props, children: children }) }) }));
    if (DEPRECATED_optionsOnFocusOnly && !isOpen && !isTriggerFocused)
        return null;
    if (!isOpen) {
        return (jsx("div", { "data-fpl-component": "primitive", style: { display: 'none' }, children: listBox }));
    }
    return (jsx(FloatingPortal, { root: rootRef ?? document.body, children: jsx(FloatingOverlay, { className: overlay, ...WHEEL_EVENT_PASSTHROUGH, ...PREVENT_EVENT_CAPTURE, ...PREVENT_KEY_CAPTURE, "data-fpl-floating-overlay": true, children: jsx(FloatingFocusManager, { context: floatingContext, modal: false, children: listBox }) }) }));
});
Container.displayName = 'SelectPrimitive.Container';
/** @see https://fpl.figma.design/primitives/single-select/ */
const Trigger = forwardRef(({ disabled, ...props }, refIn) => {
    const { getTriggerProps, setTriggerReference } = useSelectContext('Trigger');
    const ref = useMergeRefs(setTriggerReference, refIn);
    return (jsx(EventShield, { eventListeners: ['onPointerDown', 'onClick', 'onMouseDown'], display: "contents", children: jsx("button", { "data-fpl-component": "primitive", type: "button", "data-fpl-select-trigger": true, ref: ref, "aria-disabled": !!disabled, ...getTriggerProps(), ...props }) }));
});
Trigger.displayName = 'SelectPrimitive.Trigger';
const _Option = forwardRef(({ recordingKey, disabled, value, ...props }, refIn) => {
    const { recordingKey: rootRecordingKey, activeIndex, itemList, allowSelection, getItemProps, handleSelect: handleSelectIn, setOpen, handleHighlight, notifyItemRegistered, isOpen, setTriggerLabel, isTriggerFocused, isEqual, 
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- ignore any type here
    selectedValue, } = useSelectContext('Option');
    const [label, setLabel] = useState('');
    const objectRef = useRef(null);
    const clickLock = useRef(false);
    const handleSelect = useInteractionHandler((e) => {
        handleSelectIn(value, e);
    }, { eventName: 'click', recordingKey: joinKeys(rootRecordingKey, recordingKey) }, [handleSelectIn, value]);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const labelObject = useMemo(() => ({ label, value }), [label, value]);
    const { ref: innerRef, index } = useListItem({
        // Using a non-string value technically isn't allowed by the API but it works,
        // and the feature support is wip.
        // eslint-disable-next-line
        label: labelObject,
    });
    const ref = useMergeRefs(innerRef, refIn, objectRef);
    const isActive = index === activeIndex;
    const isSelected = value === selectedValue || (selectedValue !== undefined && !!isEqual?.(value, selectedValue));
    useLayoutEffect(() => {
        if (objectRef.current) {
            setLabel(getItemLabel(objectRef.current));
        }
    }, []);
    useLayoutEffect(() => {
        if (objectRef.current && isSelected) {
            setTriggerLabel(objectRef.current.innerText ?? '');
        }
    }, [isSelected, setTriggerLabel, value, selectedValue]);
    useLayoutEffect(() => {
        if (index === undefined)
            return;
        notifyItemRegistered();
    }, [index, notifyItemRegistered]);
    const onClick = (e) => {
        if (!disabled && !clickLock.current)
            handleSelect(e);
        accept(e);
        clickLock.current = false;
    };
    const handleCtrlNav = makeHandleCtrlNav(handleHighlight, {
        closeDropdown: () => setOpen(false),
    });
    const onKeyDown = (e) => {
        if (e.key === ' ' || e.key === 'Enter') {
            if (!disabled)
                handleSelect(e);
            accept(e);
        }
        handleCtrlNav(itemList.length, activeIndex, e);
    };
    const onPointerLeave = () => {
        clickLock.current = false;
    };
    const onPointerUp = (e) => {
        if (e.button !== 0)
            return;
        if (!disabled && allowSelection) {
            clickLock.current = true;
            handleSelect(e);
        }
        accept(e);
    };
    return isOpen ||
        // Render the selected option so we can extract its ref's textContents for use in Select.Trigger
        isSelected ||
        // Render if closed but focused so that typeahead works properly
        isTriggerFocused ? (jsx("li", { "data-fpl-component": "primitive", "aria-selected": isSelected, role: "option", tabIndex: isActive ? 0 : -1, "data-fpl-selected": isActive || undefined, "aria-disabled": disabled, ref: ref, ...getItemProps({
            onKeyDown,
            onClick,
            onPointerUp,
            onPointerLeave,
        }), "data-fpl-select-value": isNonNullishPrimitive(value) ? String(value) : undefined, ...props })) : null;
});
_Option.displayName = 'SelectPrimitive.Option';
/** @see https://fpl.figma.design/primitives/single-select/ */
const Option = _Option;
/** @see https://fpl.figma.design/primitives/single-select/ */
const Title = forwardRef((props, ref) => {
    const id = useProvidedId();
    // eslint-disable-next-line fpl/fpl-data-attr -- always used in parent context
    return jsx("li", { id: id, ref: ref, role: "presentation", ...props });
});
Title.displayName = 'SelectPrimitive.Title';
/** @see https://fpl.figma.design/primitives/single-select/ */
const Group = forwardRef(({ children, title, ...props }, ref) => {
    const [id, Provider] = useIdProvider();
    if (!children)
        return null;
    if (!title) {
        return (jsx("ul", { "data-fpl-component": "primitive", ref: ref, role: "none", ...props, children: children }));
    }
    return (jsx(Provider, { value: id, children: jsxs("ul", { "data-fpl-component": "primitive", ref: ref, "aria-labelledby": id, role: "group", ...props, children: [title, children] }) }));
});
Group.displayName = 'SelectPrimitive.Group';
/** @see https://fpl.figma.design/primitives/single-select/ */
function Root({ value, onChange: providedChange, isEqual, recordingKey, onSelectionChange, onOpenChange: propOnOpenChange, offsetAmount = 0, children, padding, placement = 'bottom-start', position, customMiddleware, }) {
    const listRef = useRef([]);
    const overflowRef = useRef(null);
    const scrollRef = useRef(null);
    const itemRef = useRef([]);
    // Lock that prevents selection closing immediately on mouse up
    const [allowSelection, setAllowSelection] = useState(false);
    const [open, setOpen] = useState(false);
    const [isTriggerFocused, setTriggerIsFocused] = useState(false);
    const [activeIndex, setActiveIndex] = useState(null);
    const [fallback, setFallback] = useState(false);
    const [innerOffset, setInnerOffset] = useState(0);
    const [scrollTop, setScrollTop] = useState(0);
    // Counter to trigger rerender when options register themselves
    const [updated, forceUpdate] = useState(0);
    const previousUpdated = useRef(0);
    const [triggerLabel, setTriggerLabel] = useState(null);
    const { onChange } = useInputChange({ onChange: providedChange, recordingKey });
    const handleSelect = useCallback((newValue, event = null) => {
        onChange(newValue, { event });
        setOpen(false);
    }, [onChange, setOpen]);
    const notifyItemRegistered = useCallback(() => {
        forceUpdate((n) => n + 1);
    }, []);
    const selectedIndex = useMemo(() => {
        if (!itemRef.current)
            return null;
        const index = itemRef.current.findIndex((x) => {
            const itemValue = x?.value;
            if (itemValue === value)
                return true;
            if (isEqual) {
                if (itemValue == null && value == null)
                    return true;
                if ((itemValue == null) !== (value == null))
                    return false;
                if (isEqual)
                    return isEqual(itemValue, value);
            }
            return false;
        });
        return index !== -1 ? index : null;
    }, [JSON.stringify(itemRef.current), value, isEqual]);
    const onOpenChange = (openChange) => {
        setOpen(openChange);
        if (propOnOpenChange)
            propOnOpenChange(openChange);
    };
    useLayoutEffect(() => {
        if (open)
            setFallback(false);
    }, [open]);
    useEffect(() => {
        if (!open) {
            setInnerOffset(0);
            setAllowSelection(false);
            setFallback(false);
        }
        else {
            const lockTimeout = setTimeout(() => {
                setAllowSelection(true);
            }, 300);
            return () => {
                clearTimeout(lockTimeout);
            };
        }
    }, [open]);
    const floatingPlacement = position ? getPlacementFromPosition(position) : placement;
    const positionMiddleware = useMemo(() => (position ? createPositionMiddleware({ position, padding }) : undefined), [position, padding]);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { refs, floatingStyles, context, update } = useFloating({
        placement: floatingPlacement,
        open,
        onOpenChange,
        whileElementsMounted: (...args) => {
            if (!open)
                return () => { };
            return autoUpdate(...args);
        },
        transform: true,
        middleware: customMiddleware ??
            positionMiddleware ??
            (fallback
                ? [
                    offset({ alignmentAxis: offsetAmount * -1 }),
                    flip({ padding }),
                    shift({ padding }),
                    size({
                        apply({ rects, availableWidth, availableHeight, elements }) {
                            Object.assign(elements.floating.style, {
                                minWidth: `${rects.reference.width + offsetAmount * 2}px`,
                                maxWidth: `${availableWidth}px`,
                                maxHeight: `${availableHeight}px`,
                                display: 'flex',
                                flexDirection: 'column',
                            });
                        },
                        padding,
                    }),
                ]
                : [
                    flip({ mainAxis: false, padding }),
                    inner({
                        listRef,
                        overflowRef,
                        scrollRef,
                        index: selectedIndex ?? 0,
                        offset: innerOffset,
                        padding,
                        referenceOverflowThreshold: 20,
                        onFallbackChange: (fb) => {
                            // only setFallback to true, otherwise risks infinite loop
                            // https://app.asana.com/1/10497086658021/project/1208745346228998/task/1210320345346150
                            if (fb) {
                                const bounds = scrollRef.current?.getBoundingClientRect();
                                if (bounds && bounds.height < 90)
                                    return setFallback(fb);
                                const viewport = {
                                    height: window.innerHeight,
                                };
                                // at minimum our select items height are 24px in height
                                const minVisibleHeight = 24;
                                if (bounds) {
                                    // This accounts for the scenario where the item is first in the list after scroll
                                    // positioning but the current container is placed where the selected item would be
                                    // behind the scroll arrow
                                    if (overflowRef.current?.top === 0 &&
                                        scrollRef.current?.scrollTop !== 0 &&
                                        bounds.top < 15) {
                                        return setFallback(fb);
                                    }
                                    // Calculate how much would actually be visible
                                    const visibleTop = Math.max(0, bounds.top);
                                    const visibleBottom = Math.min(viewport.height, bounds.bottom);
                                    const visibleHeight = Math.max(0, visibleBottom - visibleTop);
                                    // Fallback if not enough visible height
                                    if (visibleHeight < minVisibleHeight) {
                                        setFallback(fb);
                                    }
                                }
                            }
                        },
                        minItemsVisible: 4,
                    }),
                    offset({ alignmentAxis: offsetAmount * -1 }),
                    size({
                        apply({ rects, availableWidth, elements }) {
                            Object.assign(elements.floating.style, {
                                minWidth: `${rects.reference.width + offsetAmount * 2}px`,
                                maxWidth: `${availableWidth}px`,
                            });
                        },
                        padding,
                    }),
                ]),
    });
    const debouncedUpdate = useDebounce(update, { timeout: 200 }, [update]);
    // When "updated" (# of options) changes, call `update` to notify floating-ui
    // This ensures that the sizing/layout is always correct
    useEffect(() => {
        if (open && previousUpdated.current !== updated) {
            previousUpdated.current = updated;
            return debouncedUpdate();
        }
        previousUpdated.current = updated;
    }, [open, updated, debouncedUpdate]);
    const handleHighlight = useCallback((index) => {
        // floating-ui calls handleHighlight on mouse movements so we need to on fire onSelection change when the index itself changes
        if (onSelectionChange && activeIndex !== index) {
            if (index === null)
                onSelectionChange();
            else
                onSelectionChange(itemRef.current[index].value);
        }
        setActiveIndex(index);
    }, [onSelectionChange, itemRef, activeIndex]);
    const selectTypeahead = (index) => {
        if (index !== null)
            handleSelect(itemRef.current[index].value);
    };
    const triggerRef = refs.reference;
    let isTriggerDisabled = false;
    if (triggerRef.current) {
        if (triggerRef.current.hasAttribute('disabled') ||
            ['', 'true'].includes(triggerRef.current.getAttribute('aria-disabled'))) {
            isTriggerDisabled = true;
        }
    }
    const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
        useClick(context, { enabled: !isTriggerDisabled, event: 'mousedown' }),
        useDismiss(context),
        useInnerOffset(context, {
            // Inner offset aligns the selected option with the trigger, so it does not apply when using
            // trigger-relative positioning.
            enabled: !fallback && !position,
            onChange: setInnerOffset,
            overflowRef,
            scrollRef,
        }),
        useListNavigation(context, {
            listRef,
            activeIndex,
            selectedIndex,
            focusItemOnOpen: true,
            disabledIndices: [],
            scrollItemIntoView: {
                block: 'center',
                inline: 'center',
            },
            onNavigate: handleHighlight,
        }),
        useTypeahead(context, {
            listRef: itemRef,
            activeIndex,
            onMatch: open ? handleHighlight : selectTypeahead,
            // @ts-expect-error typeahead doesn't support generics, going to make a issue with lib
            findMatch: (list, string) => {
                const searchString = normalizeTypeaheadText(string);
                return list.find((x) => normalizeTypeaheadText(x.label).startsWith(searchString));
            },
        }),
    ]);
    const containerId = useId();
    const selectContext = useMemo(() => {
        function handleArrowScroll(amount) {
            // Positioned and fallback popups scroll the list directly.
            // In selected-option alignment mode, Floating UI manages scrolling
            // along with popup placement, so update innerOffset instead.
            if (fallback || position) {
                if (scrollRef.current) {
                    scrollRef.current.scrollTop -= amount;
                    flushSync(() => setScrollTop(scrollRef.current.scrollTop ?? 0));
                    setScrollPositionAttribute(scrollRef.current);
                }
            }
            else {
                flushSync(() => setInnerOffset((newValue) => newValue - amount));
                setScrollPositionAttribute(scrollRef.current);
            }
        }
        function getTriggerProps() {
            const referenceProps = getReferenceProps({
                'aria-controls': containerId,
                'aria-haspopup': 'listbox',
                'aria-expanded': open,
                role: 'combobox',
            });
            return {
                ...referenceProps,
                // WebKit sequential focus skips role="combobox" unless tabindex is explicit.
                tabIndex: 0,
                onFocus(e) {
                    referenceProps.onFocus?.(e);
                    setTriggerIsFocused(true);
                },
                onBlur(e) {
                    referenceProps.onBlur?.(e);
                    setTriggerIsFocused(false);
                },
            };
        }
        function getContainerProps(userProps) {
            return getFloatingProps({
                ...userProps,
                id: containerId,
            });
        }
        return {
            recordingKey,
            getTriggerProps,
            setTriggerReference: refs.setReference,
            getContainerProps,
            setContainerReference: refs.setFloating,
            getItemProps,
            setOpen,
            overflowRef,
            scrollRef,
            handleHighlight,
            setScrollTop,
            handleSelect,
            handleArrowScroll,
            notifyItemRegistered,
            floatingContext: context,
            floatingStyles: floatingStyles,
            isOpen: open,
            isTriggerFocused,
            fallback,
            itemList: itemRef.current,
            scrollTop,
            innerOffset,
            activeIndex,
            selectedIndex,
            allowSelection,
            selectedValue: value,
            triggerLabel,
            setTriggerLabel,
            isEqual,
        };
    }, [
        recordingKey,
        refs.setReference,
        refs.setFloating,
        handleHighlight,
        getItemProps,
        handleSelect,
        notifyItemRegistered,
        context,
        floatingStyles,
        open,
        isTriggerFocused,
        fallback,
        position,
        scrollTop,
        innerOffset,
        activeIndex,
        selectedIndex,
        allowSelection,
        getReferenceProps,
        containerId,
        getFloatingProps,
        updated,
        value,
        triggerLabel,
        setTriggerLabel,
        isEqual,
    ]);
    return (jsx(SelectPrimitiveContext.Provider, { value: selectContext, children: jsx(FloatingList, { elementsRef: listRef, labelsRef: itemRef, children: children }) }));
}
Root.displayName = 'SelectPrimitive.Root';
function useSelectContext(callee) {
    return useValidatedContext(SelectPrimitiveContext, callee, 'SelectPrimitive.Root');
}
function setScrollPositionAttribute(scrollElement, scrollPosition = getScrollablePopupScrollPosition(scrollElement)) {
    if (scrollElement)
        scrollElement.setAttribute('data-fpl-scroll-y', scrollPosition);
}
function initializeScrollPosition(node) {
    setScrollPositionAttribute(node, 'none');
}

export { Container, Group, Option, Root, ScrollArrow, SelectPrimitiveContext, Title, Trigger, useSelect };
//# sourceMappingURL=select-primitive.js.map
